import { useEffect, useState } from 'react';
import { calculateSplitMutation } from '@/api/calculateSplit';
import { isSplitDefined } from '@/api/getSplitDetails';
import { DEFAULT_NUMBER_OF_SPLITS } from '@/components/SplitEqualMode/SplitEqualMode';
import formatNumber from '@/utils/formatNumber';
import { useBill } from './useBill';
import { useSplitState } from './useSplitState';

interface UseCalculateSplitParams {
  tipPercentage: number;
  orderNumber: string;
  numberOfSplits?: number;
  numberOfCoveredSplits?: number;
  totalBillAmount: number;
  isNewSplitOpen: boolean;
  baseToPay?: number;
}

interface UseCalculateSplitResult {
  yourShare: number | null;
  cashback: number | null;
}

export const useCalculateSplit = ({
  tipPercentage,
  orderNumber,
  numberOfSplits,
  numberOfCoveredSplits,
  totalBillAmount,
  isNewSplitOpen,
  baseToPay,
}: UseCalculateSplitParams): UseCalculateSplitResult => {
  const [yourShare, setYourShare] = useState(0);
  const [cashback, setCashback] = useState(0);
  const { isSplitExist, splitDetails } = useSplitState({ orderNumber });
  const { data: billDetails } = useBill({
    amount: yourShare || undefined,
    selectedTipPercentage: tipPercentage,
    enabled: isNewSplitOpen || isSplitExist,
  });
  const { mutate: calculateSplit } = calculateSplitMutation();

  useEffect(() => {
    if (isSplitExist && splitDetails && yourShare === 0) {
      setYourShare(splitDetails?.onePersonSplit || 0);
    }
  }, [isSplitExist, splitDetails, yourShare]);

  useEffect(() => {
    if (billDetails && yourShare > 0) {
      setCashback(billDetails.estimatedCashback);
    }
  }, [billDetails, yourShare]);

  useEffect(() => {
    if (billDetails && isNewSplitOpen) {
      let totalAmount = totalBillAmount;
      let remainingAmount = totalBillAmount;

      if (isSplitDefined(splitDetails)) {
        totalAmount = splitDetails.totalAmount;
        remainingAmount = splitDetails.remainingAmount;
        numberOfSplits = splitDetails.numberOfSplits;
      }

      const calculateSplitParams = {
        numberOfSplits: numberOfSplits || DEFAULT_NUMBER_OF_SPLITS,
        numberOfCoveredSplits: numberOfCoveredSplits || 1,
        totalAmount,
        remainingAmount,
      };

      calculateSplit(calculateSplitParams, {
        onSuccess: response => {
          if (response && 'amountToPay' in response) {
            setYourShare(Number(formatNumber(response.amountToPay)));
          }
        },
        onError: error => {
          console.error('Error calculating split:', error);
          setYourShare(0);
        },
      });
    }
  }, [
    calculateSplit,
    isNewSplitOpen,
    totalBillAmount,
    splitDetails,
    numberOfSplits,
    numberOfCoveredSplits,
    billDetails,
  ]);

  if (!isNewSplitOpen && !isSplitExist) {
    return {
      yourShare: totalBillAmount,
      cashback: 0,
    };
  }

  if (!isSplitExist && billDetails) {
    return {
      yourShare,
      cashback: billDetails.estimatedCashback,
    };
  }

  // When split exists but is not open, use baseToPay from context
  if (isSplitExist && !isNewSplitOpen && baseToPay !== undefined) {
    return {
      yourShare: baseToPay,
      cashback: billDetails?.estimatedCashback || 0,
    };
  }

  return {
    yourShare,
    cashback,
  };
};
